<template>
  <div>
    <b-card>
      <b-card-title>الصلاحيات والأدوار الخاصة بي</b-card-title>
      <b-card-text class="text-muted">
        عرض جميع الصلاحيات والأدوار المسندة لحسابك.
      </b-card-text>

      <div
        v-if="isLoadingPermissions"
        class="text-center py-5"
      >
        <b-spinner variant="primary" />
        <p class="mt-2 text-muted">
          جاري تحميل الصلاحيات...
        </p>
      </div>

      <div v-else>
        <!-- Roles Section -->
        <div class="mb-3">
          <h5 class="mb-2">
            <feather-icon
              icon="ShieldIcon"
              size="18"
              class="mr-50"
            />
            الأدوار
          </h5>
          <div v-if="roles && roles.length > 0">
            <b-badge
              v-for="role in roles"
              :key="role.id"
              variant="light-primary"
              pill
              class="mr-1 mb-1 p-1 px-2"
            >
              <feather-icon
                icon="AwardIcon"
                size="14"
                class="mr-50"
              />
              {{ role.name }}
            </b-badge>
          </div>
          <p
            v-else
            class="text-muted mb-0"
          >
            لا توجد أدوار مسندة
          </p>
        </div>

        <hr>

        <!-- Permissions Section -->
        <div>
          <h5 class="mb-2">
            <feather-icon
              icon="LockIcon"
              size="18"
              class="mr-50"
            />
            الصلاحيات
            <b-badge
              variant="light-secondary"
              pill
              class="ml-1"
            >
              {{ permissionNames.length }}
            </b-badge>
          </h5>

          <div v-if="permissionNames && permissionNames.length > 0">
            <!-- Search Permissions -->
            <b-form-group class="mb-2">
              <b-input-group>
                <b-input-group-prepend>
                  <b-input-group-text>
                    <feather-icon icon="SearchIcon" />
                  </b-input-group-text>
                </b-input-group-prepend>
                <b-form-input
                  v-model="searchQuery"
                  placeholder="البحث في الصلاحيات..."
                />
              </b-input-group>
            </b-form-group>

            <!-- Permissions Grid -->
            <b-row>
              <b-col
                v-for="(permission, index) in filteredPermissions"
                :key="index"
                cols="12"
                md="6"
                lg="4"
                class="mb-1"
              >
                <div class="permission-item p-1 rounded d-flex align-items-center">
                  <feather-icon
                    icon="CheckCircleIcon"
                    size="16"
                    class="text-success mr-1"
                  />
                  <span>{{ permission }}</span>
                </div>
              </b-col>
            </b-row>

            <!-- No Results -->
            <div
              v-if="filteredPermissions.length === 0"
              class="text-center py-3"
            >
              <feather-icon
                icon="SearchIcon"
                size="48"
                class="text-muted mb-2"
              />
              <p class="text-muted mb-0">
                لم يتم العثور على صلاحيات تطابق "{{ searchQuery }}"
              </p>
            </div>
          </div>

          <p
            v-else
            class="text-muted mb-0"
          >
            لا توجد صلاحيات مسندة
          </p>
        </div>

        <!-- Permissions by Category (Optional grouping) -->
        <div
          v-if="permissionsByCategory && Object.keys(permissionsByCategory).length > 0"
          class="mt-3"
        >
          <hr>
          <h5 class="mb-2">
            <feather-icon
              icon="GridIcon"
              size="18"
              class="mr-50"
            />
            الصلاحيات حسب الفئة
          </h5>

          <b-card
            v-for="(permissions, category) in permissionsByCategory"
            :key="category"
            class="mb-2"
            body-class="pb-1"
          >
            <h6 class="mb-1 text-capitalize">
              {{ category }}
              <b-badge
                variant="light-secondary"
                pill
                class="ml-1"
              >
                {{ permissions.length }}
              </b-badge>
            </h6>
            <div>
              <b-badge
                v-for="(permission, index) in permissions"
                :key="index"
                variant="light-success"
                class="mr-1 mb-1"
              >
                {{ permission }}
              </b-badge>
            </div>
          </b-card>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardTitle,
  BCardText,
  BBadge,
  BSpinner,
  BRow,
  BCol,
  BFormGroup,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupText,
  BFormInput,
} from 'bootstrap-vue'
import { mapGetters } from 'vuex'

export default {
  name: 'PermissionsView',
  components: {
    BCard,
    BCardTitle,
    BCardText,
    BBadge,
    BSpinner,
    BRow,
    BCol,
    BFormGroup,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupText,
    BFormInput,
  },
  data() {
    return {
      searchQuery: '',
    }
  },
  computed: {
    ...mapGetters('profile', {
      permissions: 'permissions',
      permissionNames: 'permissionNames',
      roles: 'roles',
      roleNames: 'roleNames',
      isLoadingPermissions: 'isLoadingPermissions',
    }),

    filteredPermissions() {
      if (!this.searchQuery) {
        return this.permissionNames
      }
      const query = this.searchQuery.toLowerCase()
      return this.permissionNames.filter(permission => permission.toLowerCase().includes(query))
    },

    permissionsByCategory() {
      if (!this.permissionNames || this.permissionNames.length === 0) {
        return {}
      }

      const categories = {}
      this.permissionNames.forEach(permission => {
        // Extract category from permission name (e.g., "view users" -> "users")
        const parts = permission.split(' ')
        if (parts.length >= 2) {
          const category = parts[parts.length - 1] // Get last word as category
          if (!categories[category]) {
            categories[category] = []
          }
          categories[category].push(permission)
        } else {
          // If no category can be determined, put in "other"
          if (!categories.other) {
            categories.other = []
          }
          categories.other.push(permission)
        }
      })

      return categories
    },
  },
}
</script>

<style lang="scss" scoped>
.permission-item {
  background-color: #f8f8f8;
  border: 1px solid #efefef;
  transition: all 0.2s ease;

  &:hover {
    background-color: #e8f5e9;
    border-color: #4caf50;
  }
}

.dark-layout {
  .permission-item {
    background-color: #283046;
    border-color: #3b4253;

    &:hover {
      background-color: #2d3e50;
      border-color: #4caf50;
    }
  }
}
</style>
